import React, {FC, useEffect, useMemo, useRef} from 'react';

import {CheckIcon} from '../SvgIcon/Multimedia';

interface ControlledCheckboxProps {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
  checkBoxSize?: string; // e.g., 'w-5 h-5'
  fontSize?: string; // e.g., 'text-base'
}

/**
 * - This checkbox control the state from the parent, so it's affected by the parent state.
 * - If you need a simple checkbox and don't need to control the state from the parent,
 * use the `<Checkbox />` component instead.
 * - Set `indeterminate` to `true` if you want to show a checkbox with a checkmark and a dash.
 */
export const ControlledCheckbox: FC<ControlledCheckboxProps> = ({
  label,
  checked,
  indeterminate = false,
  onChange,
  className = '',
  checkBoxSize = 'w-5 h-5',
  fontSize = 'text-base',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const bgColorCheckbox = useMemo(() => {
    if (indeterminate || checked) {
      return 'bg-blue-500';
    }
    return 'bg-white';
  }, [indeterminate, checked]);

  return (
    <div className={`inline-block align-middle ${className}`}>
      <label
        className={`flex items-center cursor-pointer select-none gap-2 ${fontSize}`}
      >
        <span className="relative flex items-center">
          <input
            ref={inputRef}
            type="checkbox"
            checked={checked}
            onChange={e => onChange(e.target.checked)}
            className="peer appearance-none absolute opacity-0 w-0 h-0"
          />
          <span
            className={`flex justify-center items-center border-2 border-blue-500 rounded transition-colors duration-200 ${checkBoxSize} ${bgColorCheckbox}`}
          >
            {indeterminate && !checked && (
              <span className="block w-3 h-0.5 bg-white rounded-sm" />
            )}
            {checked && (
              <CheckIcon
                strokeColor="text-white"
                fillColor="text-white"
                strokeWidth={2}
              />
            )}
          </span>
        </span>
        {label}
      </label>
    </div>
  );
};
